import React, { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import whatsappLogo from '../assets/whatsapp-logo.png';

const WhatsAppFloat = () => {
  // Hooks must be called unconditionally and in the same order on every render.
  const [mounted, setMounted] = useState(false);
  const containerRef = useRef(null);

  // Require environment variables prefixed with REACT_APP_ (Create React App).
  const waNumber = process.env.REACT_APP_WHATSAPP_NUMBER;
  const rawText = process.env.REACT_APP_WHATSAPP_TEXT || '';
  const waText = encodeURIComponent(rawText);
  const href = waNumber ? `https://wa.me/${waNumber}?text=${waText}` : '';

  // Guard side effects: only create DOM container when a WA number is configured.
  useEffect(() => {
    if (!waNumber) return;
    const el = document.createElement('div');
    el.setAttribute('data-whatsapp-float', '1');
    document.body.appendChild(el);
    containerRef.current = el;
    setMounted(true);

    // Ensure responsive right-offset and update when viewport changes
    // Keep bottom (y-axis) fixed for consistent vertical alignment across devices
    const applyOffsets = () => {
      if (!containerRef.current) return;
      const isDesktop = window.matchMedia('(min-width: 768px)').matches;
      const rightOffset = isDesktop ? 'calc(env(safe-area-inset-right, 0px) + 20px)' : 'calc(env(safe-area-inset-right, 0px) + 12px)';
      // store right offset only; bottom remains fixed
      containerRef.current.dataset.right = rightOffset;
    };

    applyOffsets();
    const mq = window.matchMedia('(min-width: 768px)');
    const onChange = () => applyOffsets();
    try { mq.addEventListener ? mq.addEventListener('change', onChange) : mq.addListener(onChange); } catch (e) {}

    return () => {
      try {
        if (containerRef.current) document.body.removeChild(containerRef.current);
      } catch (e) {
        // ignore
      }
      try { mq.removeEventListener ? mq.removeEventListener('change', onChange) : mq.removeListener(onChange); } catch (e) {}
    };
  }, [waNumber]);

  const button = (
    <a
      href={href}
      target="_blank"
      rel="noopener noreferrer"
      aria-label="Chat with us on WhatsApp"
      title="Chat with us on WhatsApp"
      // use the offsets computed on the container (fallback to 20px)
      style={{
        bottom: 'calc(env(safe-area-inset-bottom, 0px) + 20px)',
        right: (containerRef.current && containerRef.current.dataset.right) || 'calc(env(safe-area-inset-right, 0px) + 20px)',
      }}
      className="fixed z-[11000] rounded-full bg-[#25D366] flex items-center justify-center shadow-lg hover:scale-110 transform transition-all duration-300 w-[55px] h-[55px] md:w-[60px] md:h-[60px]"
    >
      <img
        src={whatsappLogo}
        alt="WhatsApp"
        className="w-7 h-7 md:w-8 md:h-8 object-contain"
        aria-hidden="true"
      />
    </a>
  );

  if (!mounted || !containerRef.current) return null;
  return createPortal(button, containerRef.current);
};

export default WhatsAppFloat;
